import { Component, OnInit, signal } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc } from 'firebase/firestore';
import { db } from 'src/app/app.component';
import { Particularity } from 'src/app/interfaces';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-manage-particularities',
  templateUrl: './manage-particularities.component.html',
  styleUrl: './manage-particularities.component.scss',
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatSlideToggle,
    MatDialogActions,
    MatButton,
    MatDialogClose,
  ],
})
export class ManageParticularitiesComponent implements OnInit {
  orgsForm: UntypedFormGroup;
  orgs = signal<any[]>([]);
  globalParticularities = signal<Particularity[]>([]);
  saving = signal(false);

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<ManageParticularitiesComponent>,
  ) {}

  async ngOnInit(): Promise<void> {
    this.orgsForm = this.fb.group({
      org: ['', Validators.required],
    });
    await this.getGlobalParticularities();
    await this.getOrgs();
    this.orgsForm.get('org').valueChanges.subscribe((org) => {
      this.resetParticularities();
      this.setActiveOrgParticularities(org);
    });
  }

  async save() {
    if (this.saving()) {
      return;
    }
    this.saving.set(true);
    const org = this.orgsForm.get('org').value;
    const promises = [];
    for (const particularity of this.globalParticularities()) {
      const iconRef = doc(db, `organisations/${org.id}/particularities/${particularity.id}`);
      const iconDoc = await getDoc(iconRef);
      if (!iconDoc.exists() && this.orgsForm.value[particularity.id]) {
        promises.push(
          setDoc(iconRef, { ...particularity, active: this.orgsForm.value[particularity.id] }),
        );
      } else if (iconDoc.exists()) {
        const iconData = iconDoc.data();
        if (this.orgsForm.value[particularity.id] !== iconData.active) {
          promises.push(updateDoc(iconRef, { active: this.orgsForm.value[particularity.id] }));
        }
      }
    }
    try {
      await Promise.all(promises);
      this.saving.set(false);
      this.dialogRef.close();
    } catch (e) {
      console.log('error while updating particularities: ', e);
      this.saving.set(false);
    }
  }

  resetParticularities() {
    this.globalParticularities().forEach((particularity) => {
      this.orgsForm.controls[particularity.id].setValue(false);
    });
  }

  setActiveOrgParticularities(org) {
    org.particularities.forEach((particularity) => {
      if (
        (particularity.active == null || particularity.active) &&
        this.orgsForm.controls[particularity.id]
      ) {
        this.orgsForm.controls[particularity.id].setValue(true);
      }
    });
  }

  async getGlobalParticularities() {
    const particularityDocs = (await getDocs(collection(db, `particularities`))).docs;
    const particularitiesArray: Particularity[] = [];
    particularityDocs.forEach((particularityDoc) => {
      const particularityData = particularityDoc.data() as Particularity;
      particularitiesArray.push({ ...particularityData, id: particularityDoc.id });

      this.orgsForm.addControl(particularityDoc.id, new UntypedFormControl(false));
    });
    particularitiesArray.sort((a, b) => a.name.localeCompare(b.name));
    this.globalParticularities.set(particularitiesArray);
  }

  async getOrgs() {
    const orgDocs = (await getDocs(collection(db, `organisations`))).docs;
    const orgsArray: any[] = [];
    for (const doc of orgDocs) {
      const particularityDocs = (
        await getDocs(collection(db, `organisations/${doc.id}/particularities`))
      ).docs;
      const orgParticularities: Particularity[] = [];
      particularityDocs.forEach((particularityDoc) => {
        const particularityData = particularityDoc.data() as Particularity;
        orgParticularities.push({ ...particularityData, id: particularityDoc.id });
      });
      orgParticularities.sort((a, b) => a.name.localeCompare(b.name));
      orgsArray.push({ name: doc.data().name, id: doc.id, particularities: orgParticularities });
    }
    orgsArray.sort((a, b) => a.name.localeCompare(b.name));
    this.orgs.set(orgsArray);
  }
}
